import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import {
  DEFAULT_PAGE,
  DEFAULT_SIZE,
  PaginationConstants,
} from 'lib/const/PaginationConstants';
import { PRONUNCIATIONS_SORT_KEYS } from './constants';
import { useQueryPronunciations } from 'lib/api/pronunciations/useQueryPronunciations';
import { PronunciationList } from './components/PronunciationsList';
import { CreatePronunciationModal } from './modal/CreatePronunciationModal';
import { useCreatePronunciationMutation } from 'lib/api/pronunciations/useCreatePronunciationMutation';
import { useUploadFile } from 'lib/hooks/useUploadFile';
import { useToastError } from 'lib/hooks';
import { useTranslation } from 'react-i18next';

export interface CreatePronunciationValues {
  title: string;
  droppedAudioFile: File | null;
  recordedAudioFile: File | null;
}

export const Pronunciations = forwardRef((_, ref) => {
  const { t } = useTranslation();
  const { uploadFile } = useUploadFile();
  const { showError } = useToastError();
  // modal
  const [isModalOpen, setIsModalOpen] = useState(false);

  // query
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [pronunciationSortKey, setPronunciationtSortKey] =
    useState<PRONUNCIATIONS_SORT_KEYS | null>(null);
  const [pronunciationSortOrder, setPronunciationSortOrder] = useState(
    PaginationConstants.DESCENDING
  );

  const onPaginationChange = async ({
    page,
    size,
  }: {
    page: number;
    size: number;
  }) => {
    setPage(page);
    setSize(size);
  };

  const onSortingUpdate = (sortKey: string, order: PaginationConstants) => {
    setPronunciationtSortKey(sortKey as PRONUNCIATIONS_SORT_KEYS);
    setPronunciationSortOrder(order);
    setPage(DEFAULT_PAGE);
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const params = {
    sortKey: pronunciationSortKey,
    order: pronunciationSortOrder || PaginationConstants.ASCENDING,
    start: page * size,
    limit: size,
  };

  const { data, isLoading: isLoadingPronunciations } =
    useQueryPronunciations(params);
  const pronunciations = data?.customerPhonetics || [];
  const count = data?.count || 0;

  const { mutateAsync: createPronunciationMutation } =
    useCreatePronunciationMutation(toggleModal);

  const openAddModal = useCallback(() => {
    toggleModal();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openAddModal,
    }),
    [openAddModal]
  );

  const handleSave = async (data: CreatePronunciationValues) => {
    const { title, droppedAudioFile, recordedAudioFile } = data;
    const audioFile = droppedAudioFile || recordedAudioFile;
    if (!audioFile) {
      return;
    }
    const url = await uploadFile(audioFile);
    if (!url) {
      showError(t('Failed to get URL from file upload.'));
      return;
    }

    const pronunciation = {
      text: title,
      audioS3Url: url,
    };
    await createPronunciationMutation(pronunciation);
  };

  return (
    <>
      <PronunciationList
        data={pronunciations || []}
        isLoadingPronunciations={isLoadingPronunciations}
        count={count}
        page={page}
        size={size}
        sortKey={pronunciationSortKey}
        order={pronunciationSortOrder}
        onPaginationChange={onPaginationChange}
        onSortingUpdate={onSortingUpdate}
        handleCreateClick={toggleModal}
      />
      {isModalOpen && (
        <CreatePronunciationModal onClose={toggleModal} onSave={handleSave} />
      )}
    </>
  );
});
